import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import LoadingScreen from "./LoadingScreen";
import RoddoRadioMapWidget from "./RoddoRadioMapWidget";
import RoddoRadioNowPlayingBanner from "./RoddoRadioNowPlayingBanner";
import type { UserProfile } from "../../types";
import {
  getMapAvatarIconMetrics,
  renderMapAvatarMarkerHtml,
} from "./avatar/MapAvatarMarker";

type GlobeTarget = {
  lat: number;
  lng: number;
  zoom?: number;
  label?: string;
};

type BigBlueGlobeViewProps = {
  isDarkMode?: boolean;
  coords?: { lat: number; lon?: number; lng?: number } | null;
  initialTarget?: GlobeTarget | null;
  launchMode?: "firstOpen" | "zoomOut";
  onDive: (target: GlobeTarget) => void;
  onOpenMap?: () => void;

  /** V4.13.6: optional profile context lets GlobeView reuse the same avatar badge as Big Blue Map. */
  profile?: Partial<UserProfile> | null;
  userProfile?: Partial<UserProfile> | null;
  currentUserProfile?: Partial<UserProfile> | null;
  profilePhotoUrl?: string | null;
};

const EARTH_MODEL_URL = "/models/earth/Earth.glb";

let roddoCachedEarthModel: THREE.Object3D | null = null;
let roddoEarthLoadPromise: Promise<THREE.Object3D> | null = null;
let roddoEarthLoadedOnce = false;

const cloneEarthModelForScene = (source: THREE.Object3D) => {
  const clone = source.clone(true);

  clone.traverse((child: any) => {
    if (!child?.isMesh) return;

    if (child.geometry?.clone) child.geometry = child.geometry.clone();

    if (Array.isArray(child.material)) {
      child.material = child.material.map((m: any) => (m?.clone ? m.clone() : m));
    } else if (child.material?.clone) {
      child.material = child.material.clone();
    }
  });

  return clone;
};

const loadEarthModelOnce = () => {
  if (roddoCachedEarthModel) return Promise.resolve(roddoCachedEarthModel);
  if (roddoEarthLoadPromise) return roddoEarthLoadPromise;

  roddoEarthLoadPromise = new Promise<THREE.Object3D>((resolve, reject) => {
    const loader = new GLTFLoader();
    loader.load(
      EARTH_MODEL_URL,
      (gltf) => {
        roddoCachedEarthModel = gltf.scene;
        roddoEarthLoadedOnce = true;
        resolve(gltf.scene);
      },
      undefined,
      (error) => {
        roddoEarthLoadPromise = null;
        reject(error);
      },
    );
  });

  return roddoEarthLoadPromise;
};


export const preloadBigBlueGlobeAssets = async () => {
  await loadEarthModelOnce();
};

const DEFAULT_TARGET: GlobeTarget = {
  lat: 34.0522,
  lng: -118.2437,
  zoom: 9,
  label: "Pacific Waters",
};

const EARTH_MODEL_FIT = 3.15;
const STAR_POOL_COUNT = 1800;
const GLOBE_SIZE_CSS =
  "min(92vw, 500px, calc(100% - 12px), calc((var(--roddo-app-vh, 100vh) - var(--roddo-header-h) - var(--roddo-nav-h)) * 0.64))";

const FALLBACK_LOCATION_MARKER_RADIUS = 1.61;
const LOCATION_MARKER_SURFACE_OFFSET = 0.035;
const EARTH_TEXTURE_LONGITUDE_OFFSET_DEG = 180;

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

const normalizeLng = (lng: number) => {
  let out = lng;
  while (out > 180) out -= 360;
  while (out < -180) out += 360;
  return out;
};

const coordsToTarget = (
  coords?: { lat: number; lon?: number; lng?: number } | null,
  fallback: GlobeTarget = DEFAULT_TARGET,
): GlobeTarget => {
  const lat = Number(coords?.lat);
  const lng = Number(coords?.lng ?? coords?.lon);
  if (Number.isFinite(lat) && Number.isFinite(lng))
    return { lat, lng, zoom: 10, label: "Current Waters" };
  return fallback;
};

const USER_AVATAR_FALLBACK =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96">
       <rect width="96" height="96" rx="24" fill="#cbd5e1"/>
       <circle cx="48" cy="38" r="16" fill="#94a3b8"/>
       <path d="M20 86c6-18 20-26 28-26s22 8 28 26" fill="#94a3b8"/>
     </svg>`,
  );

const getProfileAvatarBaseUrl = (profile: any): string => {
  const first = [
    profile?.avatarUrl,
    profile?.avatar,
    profile?.photoURL,
    profile?.photoUrl,
    profile?.profilePhotoUrl,
    profile?.imageUrl,
  ].find((value) => typeof value === "string" && value.trim());

  return typeof first === "string" ? first.trim() : "";
};

const getProfileAvatarUrl = (profile: any, explicitPhotoUrl?: string | null): string => {
  const explicit = typeof explicitPhotoUrl === "string" ? explicitPhotoUrl.trim() : "";
  if (explicit) return explicit;

  const base = getProfileAvatarBaseUrl(profile);
  return base || USER_AVATAR_FALLBACK;
};

const GLOBE_AVATAR_RENDER_SCALE = 1;
const GLOBE_AVATAR_AXIS_SCALE = 0.525;
const GLOBE_AVATAR_HORIZON_HIDE_DOT = 0.045;
const GLOBE_AVATAR_HORIZON_FULL_DOT = 0.36;
const GLOBE_AVATAR_MIN_DEPTH_SCALE = 0.66;
const GLOBE_AVATAR_MAX_DEPTH_SCALE = 1.00;
const GLOBE_AVATAR_AXIS_SAMPLE = 0.26;
const GLOBE_AVATAR_PULSE_AMOUNT = 0.045;

const smoothstep = (edge0: number, edge1: number, value: number) => {
  const t = clamp((value - edge0) / Math.max(0.0001, edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
};

const projectWorldToGlobeScreen = (
  worldPosition: THREE.Vector3,
  camera: THREE.Camera,
  width: number,
  height: number,
) => {
  const projected = worldPosition.clone().project(camera);
  return {
    projected,
    x: (projected.x * 0.5 + 0.5) * width,
    y: (-projected.y * 0.5 + 0.5) * height,
  };
};

const getSafeScreenAxis = (
  from: { x: number; y: number },
  to: { x: number; y: number },
  fallback: { x: number; y: number },
) => {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);
  if (!Number.isFinite(length) || length < 0.001) return fallback;
  return { x: dx / length, y: dy / length };
};

const geoLngToModelLng = (lng: number) =>
  normalizeLng(Number(lng || 0) - EARTH_TEXTURE_LONGITUDE_OFFSET_DEG);

const modelLngToGeoLng = (lng: number) =>
  normalizeLng(Number(lng || 0) + EARTH_TEXTURE_LONGITUDE_OFFSET_DEG);

const targetToRotation = (target: GlobeTarget) => {
  const modelLng = geoLngToModelLng(Number(target.lng || 0));
  return {
    x: THREE.MathUtils.degToRad(clamp(Number(target.lat || 0) * 0.35, -24, 24)),
    y: THREE.MathUtils.degToRad(normalizeLng(-modelLng)),
  };
};

const rotationToTarget = (rotation: { x: number; y: number }): GlobeTarget => {
  const modelLng = normalizeLng(-THREE.MathUtils.radToDeg(rotation.y));
  return {
    lat: clamp(THREE.MathUtils.radToDeg(rotation.x) / 0.35, -75, 75),
    lng: modelLngToGeoLng(modelLng),
    zoom: 7,
    label: "Globe Target",
  };
};

const latLngToSpherePosition = (
  lat: number,
  lng: number,
  radius = FALLBACK_LOCATION_MARKER_RADIUS,
) => {
  const latRad = THREE.MathUtils.degToRad(clamp(lat, -89.5, 89.5));
  const lngRad = THREE.MathUtils.degToRad(geoLngToModelLng(lng));

  return new THREE.Vector3(
    radius * Math.cos(latRad) * Math.sin(lngRad),
    radius * Math.sin(latRad),
    radius * Math.cos(latRad) * Math.cos(lngRad),
  );
};

const getLatLngSurfaceFrame = (target: GlobeTarget | null) => {
  if (!target) return null;

  const latRad = THREE.MathUtils.degToRad(clamp(Number(target.lat || 0), -89.5, 89.5));
  const lngRad = THREE.MathUtils.degToRad(geoLngToModelLng(Number(target.lng || 0)));

  const east = new THREE.Vector3(
    Math.cos(lngRad),
    0,
    -Math.sin(lngRad),
  ).normalize();

  const north = new THREE.Vector3(
    -Math.sin(latRad) * Math.sin(lngRad),
    Math.cos(latRad),
    -Math.sin(latRad) * Math.cos(lngRad),
  ).normalize();

  // CSS local +Y points downward; keep both north and south available so
  // globe overlays can choose between clean rotation and surface-matrix modes.
  const south = north.clone().multiplyScalar(-1);

  return { east, north, south };
};

const makeAtmosphereRing = ({
  inner,
  peak,
  outer,
  opacity,
  color,
  z = -0.24,
}: {
  inner: number;
  peak: number;
  outer: number;
  opacity: number;
  color: number;
  z?: number;
}) => {
  const geometry = new THREE.RingGeometry(inner, outer, 192, 1);

  const material = new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: new THREE.Color(color) },
      uInner: { value: inner },
      uPeak: { value: peak },
      uOuter: { value: outer },
      uOpacity: { value: opacity },
    },
    vertexShader: `
      varying float vRadius;
      void main() {
        vRadius = length(position.xy);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 uColor;
      uniform float uInner;
      uniform float uPeak;
      uniform float uOuter;
      uniform float uOpacity;
      varying float vRadius;
      void main() {
        float innerFade = smoothstep(uInner, uPeak, vRadius);
        float outerFade = 1.0 - smoothstep(uPeak, uOuter, vRadius);
        float alpha = innerFade * outerFade * uOpacity;
        if (alpha <= 0.002) discard;
        gl_FragColor = vec4(uColor, alpha);
      }
    `,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthTest: false,
    depthWrite: false,
    side: THREE.DoubleSide,
  });

  const ring = new THREE.Mesh(geometry, material);
  ring.position.set(0, 0, z);
  ring.renderOrder = -30;
  return ring;
};

const makeAtmosphereGroup = () => {
  const group = new THREE.Group();
  group.name = "RoddoFinalCinematicAtmosphere";

  group.add(makeAtmosphereRing({ inner: 1.56, peak: 1.74, outer: 2.12, opacity: 0.20, color: 0x1f8cff, z: -0.30 }));
  group.add(makeAtmosphereRing({ inner: 1.52, peak: 1.66, outer: 1.92, opacity: 0.36, color: 0x67e8f9, z: -0.26 }));
  group.add(makeAtmosphereRing({ inner: 1.50, peak: 1.585, outer: 1.74, opacity: 0.24, color: 0xc9fbff, z: -0.22 }));

  return group;
};

const makeLocationMarker = (
  target: GlobeTarget,
  surfaceRadius = FALLBACK_LOCATION_MARKER_RADIUS,
) => {
  const marker = new THREE.Group();
  marker.name = "RoddoCurrentLocationMarker";

  const position = latLngToSpherePosition(
    Number(target.lat),
    Number(target.lng),
    Math.max(0.1, surfaceRadius + LOCATION_MARKER_SURFACE_OFFSET),
  );
  const normal = position.clone().normalize();

  // V4.14.1: the visible current-location beacon is now the DOM avatar itself.
  // Keep this Three.js group as the Earth-locked math anchor only; do not add
  // the old glow/ring/pin meshes, or the avatar will duplicate the beacon.
  marker.position.copy(position);
  marker.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), normal);

  return marker;
};
const disposeObject = (object: THREE.Object3D) => {
  object.traverse((child: any) => {
    if (child.geometry) child.geometry.dispose?.();
    const materials = Array.isArray(child.material)
      ? child.material
      : child.material
        ? [child.material]
        : [];
    materials.forEach((material: any) => {
      Object.keys(material || {}).forEach((key) => {
        const value = material[key];
        if (
          value &&
          typeof value === "object" &&
          typeof value.dispose === "function"
        ) {
          value.dispose();
        }
      });
      material.dispose?.();
    });
  });
};

const rebuildLocationMarker = (
  earthGroup: THREE.Group | null,
  existingMarker: THREE.Group | null,
  target: GlobeTarget | null,
  surfaceRadius: number,
) => {
  if (existingMarker?.parent) existingMarker.parent.remove(existingMarker);
  if (existingMarker) disposeObject(existingMarker);
  if (!earthGroup || !target) return null;

  const marker = makeLocationMarker(target, surfaceRadius);
  earthGroup.add(marker);
  return marker;
};

const BigBlueGlobeView: React.FC<BigBlueGlobeViewProps> = ({
  isDarkMode = false,
  coords,
  initialTarget,
  onDive,
  profile,
  userProfile,
  currentUserProfile,
  profilePhotoUrl,
}) => {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const visualMotionRef = useRef<HTMLDivElement | null>(null);
  const globeAvatarOverlayRef = useRef<HTMLDivElement | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const earthGroupRef = useRef<THREE.Group | null>(null);
  const earthModelRef = useRef<THREE.Object3D | null>(null);
  const locationMarkerRef = useRef<THREE.Group | null>(null);
  const globeSurfaceRadiusRef = useRef(FALLBACK_LOCATION_MARKER_RADIUS);
  const targetRotationRef = useRef({ x: 0, y: 0 });
  const velocityRef = useRef({ x: 0, y: 0 });
  const pointerRef = useRef<{ id: number; x: number; y: number; moved: boolean } | null>(null);
  const frameRef = useRef<number | null>(null);
  const diveTimerRef = useRef<number | null>(null);

  const baseTarget = useMemo<GlobeTarget>(() => {
    const initialLat = Number(initialTarget?.lat);
    const initialLng = Number(initialTarget?.lng);
    if (Number.isFinite(initialLat) && Number.isFinite(initialLng)) {
      return {
        lat: initialLat,
        lng: initialLng,
        zoom: Number(initialTarget?.zoom ?? 8),
        label: initialTarget?.label || "Current View",
      };
    }
    return coordsToTarget(coords);
  }, [coords, initialTarget]);

  const currentLocationTarget = useMemo<GlobeTarget | null>(() => {
    const lat = Number(coords?.lat);
    const lng = Number(coords?.lng ?? coords?.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng, zoom: 10, label: "Your Location" };
  }, [coords]);

  const avatarProfile = useMemo<Partial<UserProfile> | null>(() => {
    return profile || currentUserProfile || userProfile || null;
  }, [profile, currentUserProfile, userProfile]);

  const globeAvatarPhotoUrl = useMemo(
    () => getProfileAvatarUrl(avatarProfile, profilePhotoUrl),
    [avatarProfile, profilePhotoUrl],
  );

  const globeAvatarMetrics = useMemo(
    () => getMapAvatarIconMetrics(GLOBE_AVATAR_RENDER_SCALE),
    [],
  );

  const globeAvatarHtml = useMemo(
    () =>
      renderMapAvatarMarkerHtml({
        profile: avatarProfile,
        profilePhotoUrl: globeAvatarPhotoUrl,
        isDark: isDarkMode,
        avatarScale: GLOBE_AVATAR_RENDER_SCALE,
      }),
    [avatarProfile, globeAvatarPhotoUrl, isDarkMode],
  );

  const selectedTargetRef = useRef<GlobeTarget>(baseTarget);
  const currentLocationTargetRef = useRef<GlobeTarget | null>(currentLocationTarget);
  const [isDiving, setIsDiving] = useState(false);
  const [isLoaded, setIsLoaded] = useState(() => roddoEarthLoadedOnce);
  const [loadError, setLoadError] = useState<string | null>(null);

  const shellBg = isDarkMode
    ? "linear-gradient(180deg, #090D18 0%, #0D1322 52%, #090D18 100%)"
    : "linear-gradient(180deg, #07142B 0%, #0A1730 52%, #07142B 100%)";

  const doDive = useCallback(
    (target?: GlobeTarget | null, opts?: { preferUserLocation?: boolean }) => {
      if (isDiving) return;

      // The premium path: double-clicking Earth should feel like diving into
      // the angler's real water, not into an arbitrary default map center.
      // If Android/Web has already given us a current location, use that as
      // the dive target. Otherwise fall back to the visible/clicked globe target.
      const diveTarget =
        opts?.preferUserLocation && currentLocationTargetRef.current
          ? currentLocationTargetRef.current
          : target || selectedTargetRef.current;

      const lat = Number(diveTarget?.lat);
      const lng = Number((diveTarget as any)?.lng ?? (diveTarget as any)?.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

      setIsDiving(true);
      diveTimerRef.current = window.setTimeout(() => {
        onDive({
          lat,
          lng,
          zoom: Number(diveTarget?.zoom ?? 13),
          label:
            opts?.preferUserLocation && currentLocationTargetRef.current
              ? "Your Location"
              : diveTarget?.label || "Globe Target",
        });
      }, 520);
    },
    [isDiving, onDive],
  );

  useEffect(() => {
    targetRotationRef.current = targetToRotation(baseTarget);
    selectedTargetRef.current = baseTarget;

    if (earthGroupRef.current) {
      earthGroupRef.current.rotation.x = targetRotationRef.current.x;
      earthGroupRef.current.rotation.y = targetRotationRef.current.y;
    }
  }, [baseTarget]);

  useEffect(() => {
    currentLocationTargetRef.current = currentLocationTarget;
    if (!earthGroupRef.current) return;

    locationMarkerRef.current = rebuildLocationMarker(
      earthGroupRef.current,
      locationMarkerRef.current,
      currentLocationTarget,
      globeSurfaceRadiusRef.current,
    );
  }, [currentLocationTarget]);

  useEffect(() => {
    targetRotationRef.current = targetToRotation(baseTarget);

    const mount = mountRef.current;
    if (!mount) return;

    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;
    const scene = new THREE.Scene();

    const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 100);
    camera.position.set(0, 0, 5.4);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.setClearColor(0x000000, 0);
    renderer.domElement.style.display = "block";
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";
    renderer.domElement.style.touchAction = "none";
    mount.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const earthGroup = new THREE.Group();
    earthGroup.rotation.x = targetRotationRef.current.x;
    earthGroup.rotation.y = targetRotationRef.current.y;
    earthGroupRef.current = earthGroup;
    scene.add(earthGroup);

    if (currentLocationTarget) {
      locationMarkerRef.current = rebuildLocationMarker(
        earthGroup,
        locationMarkerRef.current,
        currentLocationTarget,
        globeSurfaceRadiusRef.current,
      );
    }

    scene.add(new THREE.AmbientLight(0x5f8cff, 1.08));

    const sun = new THREE.DirectionalLight(0xffffff, 3.85);
    sun.position.set(-3.7, 2.6, 5.6);
    scene.add(sun);

    const coolRim = new THREE.DirectionalLight(0x77ddff, 1.18);
    coolRim.position.set(3.9, 0.6, 3.6);
    scene.add(coolRim);

    const fill = new THREE.DirectionalLight(0x7dd3fc, 0.62);
    fill.position.set(3.8, -1.4, 2.2);
    scene.add(fill);

    scene.add(makeAtmosphereGroup());

    const positions = new Float32Array(STAR_POOL_COUNT * 3);
    for (let i = 0; i < STAR_POOL_COUNT; i += 1) {
      const radius = 16 + Math.random() * 26;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = radius * Math.cos(phi) - 8;
    }

    const starsGeometry = new THREE.BufferGeometry();
    starsGeometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));

    const starsMaterial = new THREE.PointsMaterial({
      color: 0xdff7ff,
      size: 0.02,
      transparent: true,
      opacity: 0.82,
      depthWrite: false,
    });

    const stars = new THREE.Points(starsGeometry, starsMaterial);
    scene.add(stars);

    const resize = () => {
      const rect = mount.getBoundingClientRect();
      const width = Math.max(300, Math.round(rect.width || mount.clientWidth || 320));
      const height = Math.max(300, Math.round(rect.height || mount.clientHeight || 320));

      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.setSize(width, height, false);

      const tallness = height / Math.max(width, 1);
      const visibleStarCount = Math.round(
        clamp(820 + tallness * 220 + (width * height) / 1450, 920, STAR_POOL_COUNT),
      );
      starsGeometry.setDrawRange(0, visibleStarCount);
      starsMaterial.opacity = clamp(0.70 + (tallness - 1.2) * 0.08, 0.70, 0.88);
    };

    let resizeRaf = 0;
    const requestResize = () => {
      if (resizeRaf) window.cancelAnimationFrame(resizeRaf);
      resizeRaf = window.requestAnimationFrame(() => {
        resizeRaf = 0;
        resize();
      });
    };

    resize();
    requestResize();

    const resizeObserver = new ResizeObserver(() => requestResize());
    resizeObserver.observe(mount);

    const resizeSoon = window.setTimeout(requestResize, 80);
    const resizeLater = window.setTimeout(requestResize, 260);
    const resizeAfterLoad = window.setTimeout(requestResize, 650);
    window.addEventListener("resize", requestResize);
    window.addEventListener("orientationchange", requestResize);

    let modelInstallCancelled = false;

    const installEarthModel = (source: THREE.Object3D) => {
      if (modelInstallCancelled) return;

      const model = cloneEarthModelForScene(source);
      earthModelRef.current = model;

      const box = new THREE.Box3().setFromObject(model);
      const size = new THREE.Vector3();
      const center = new THREE.Vector3();

      box.getSize(size);
      box.getCenter(center);
      model.position.sub(center);

      const maxSize = Math.max(size.x, size.y, size.z) || 1;
      const scale = EARTH_MODEL_FIT / maxSize;
      model.scale.setScalar(scale);

      globeSurfaceRadiusRef.current = FALLBACK_LOCATION_MARKER_RADIUS;

      model.traverse((child: any) => {
        if (child.isMesh) {
          child.castShadow = false;
          child.receiveShadow = false;

          if (child.material) {
            const materials = Array.isArray(child.material) ? child.material : [child.material];
            materials.forEach((material: any) => {
              material.needsUpdate = true;
            });
          }
        }
      });

      earthGroup.add(model);

      locationMarkerRef.current = rebuildLocationMarker(
        earthGroup,
        locationMarkerRef.current,
        currentLocationTargetRef.current,
        globeSurfaceRadiusRef.current,
      );

      requestResize();
      setIsLoaded(true);
    };

    loadEarthModelOnce()
      .then(installEarthModel)
      .catch((error) => {
        if (modelInstallCancelled) return;
        console.warn("[BigBlueGlobe] Failed to load Earth.glb", error);
        setLoadError("Could not load Earth.glb");
      });

    const animate = () => {
      frameRef.current = window.requestAnimationFrame(animate);

      const now = performance.now();
      const group = earthGroupRef.current;

      if (group) {
        if (!pointerRef.current) {
          targetRotationRef.current.x += velocityRef.current.x;
          targetRotationRef.current.y += velocityRef.current.y + 0.0012;
          velocityRef.current.x *= 0.935;
          velocityRef.current.y *= 0.935;
        }

        group.rotation.x += (targetRotationRef.current.x - group.rotation.x) * 0.12;
        group.rotation.y += (targetRotationRef.current.y - group.rotation.y) * 0.12;

        selectedTargetRef.current = rotationToTarget({
          x: group.rotation.x,
          y: group.rotation.y,
        });
      }

      const motionNode = visualMotionRef.current;
      if (motionNode) {
        if (reduceMotion || isDiving) {
          motionNode.style.transform = "translate3d(0, 0, 0) scale(1)";
        } else {
          const breathe = Math.sin(now * 0.00072);
          const drift = Math.sin(now * 0.00048 + 1.35);

          const scale = 1 + breathe * 0.0032;
          const y = drift * 1.15;

          motionNode.style.transform = `translate3d(0, ${y.toFixed(3)}px, 0) scale(${scale.toFixed(5)})`;
        }
      }

      const locationMarker = locationMarkerRef.current;
      if (locationMarker) {
        const pulse = (Math.sin(now * 0.0036) + 1) / 2;
        const breathe = 0.5 - Math.cos(pulse * Math.PI) * 0.5;

        const overlay = globeAvatarOverlayRef.current;
        if (overlay && cameraRef.current && mountRef.current) {
          const width = renderer.domElement.clientWidth || mountRef.current.clientWidth || 320;
          const height = renderer.domElement.clientHeight || mountRef.current.clientHeight || 320;
          const anchorWorldPosition = new THREE.Vector3();

          // V4.14.3: make the user avatar the beacon itself. It stays round and
          // readable, pulses like the old beacon, and rotates from the globe's
          // north/east axis instead of being skewed into a surface card.
          locationMarker.getWorldPosition(anchorWorldPosition);

          const globeCenter = new THREE.Vector3();
          earthGroupRef.current?.getWorldPosition(globeCenter);

          const surfaceNormal = anchorWorldPosition.clone().sub(globeCenter).normalize();
          const toCamera = cameraRef.current.position.clone().sub(anchorWorldPosition).normalize();
          const cameraDot = surfaceNormal.dot(toCamera);
          const horizonFade = smoothstep(
            GLOBE_AVATAR_HORIZON_HIDE_DOT,
            GLOBE_AVATAR_HORIZON_FULL_DOT,
            cameraDot,
          );

          const anchorScreen = projectWorldToGlobeScreen(anchorWorldPosition, cameraRef.current, width, height);
          const onScreen =
            anchorScreen.projected.z > -1 &&
            anchorScreen.projected.z < 1 &&
            anchorScreen.projected.x > -1.18 &&
            anchorScreen.projected.x < 1.18 &&
            anchorScreen.projected.y > -1.18 &&
            anchorScreen.projected.y < 1.18;

          if (horizonFade > 0.01 && onScreen) {
            const surfaceFrame = getLatLngSurfaceFrame(currentLocationTargetRef.current);
            const earthQuaternion = new THREE.Quaternion();
            const markerQuaternion = new THREE.Quaternion();
            earthGroupRef.current?.getWorldQuaternion(earthQuaternion);
            locationMarker.getWorldQuaternion(markerQuaternion);

            const northWorld = (surfaceFrame?.north
              ? surfaceFrame.north.clone().applyQuaternion(earthQuaternion)
              : new THREE.Vector3(0, 1, 0).applyQuaternion(markerQuaternion)
            ).normalize();

            const northScreen = projectWorldToGlobeScreen(
              anchorWorldPosition.clone().add(northWorld.multiplyScalar(GLOBE_AVATAR_AXIS_SAMPLE)),
              cameraRef.current,
              width,
              height,
            );
            const northAxis = getSafeScreenAxis(anchorScreen, northScreen, { x: 0, y: -1 });

            // CSS rotation maps the avatar's visual top (-Y) onto geographic north.
            const rotateRad = Math.atan2(northAxis.y, northAxis.x) + Math.PI / 2;
            const depthScale = clamp(
              GLOBE_AVATAR_MIN_DEPTH_SCALE + horizonFade * (GLOBE_AVATAR_MAX_DEPTH_SCALE - GLOBE_AVATAR_MIN_DEPTH_SCALE),
              GLOBE_AVATAR_MIN_DEPTH_SCALE,
              GLOBE_AVATAR_MAX_DEPTH_SCALE,
            );
            const pulseScale = reduceMotion ? 1 : 1 + breathe * GLOBE_AVATAR_PULSE_AMOUNT;
            const avatarScale = GLOBE_AVATAR_AXIS_SCALE * depthScale * pulseScale;
            const anchorX = globeAvatarMetrics.iconAnchor[0];
            const anchorY = globeAvatarMetrics.iconAnchor[1];
            const opacity = clamp(0.14 + horizonFade * 0.86, 0, 1);

            overlay.style.opacity = opacity.toFixed(3);
            overlay.style.transform = [
              `translate3d(${anchorScreen.x.toFixed(2)}px, ${anchorScreen.y.toFixed(2)}px, 0)`,
              `rotate(${rotateRad.toFixed(5)}rad)`,
              `scale(${avatarScale.toFixed(4)})`,
              `translate3d(${-anchorX}px, ${-anchorY}px, 0)`,
            ].join(" ");
            overlay.style.setProperty("--roddo-globe-avatar-depth", depthScale.toFixed(3));
            overlay.style.setProperty("--roddo-globe-avatar-scale", avatarScale.toFixed(3));
            overlay.style.setProperty("--roddo-globe-avatar-horizon", horizonFade.toFixed(3));
            overlay.style.setProperty("--roddo-globe-avatar-plane", "1");
            overlay.style.setProperty("--roddo-globe-avatar-pulse", breathe.toFixed(3));
            overlay.style.setProperty("--roddo-globe-avatar-pulse-scale", pulseScale.toFixed(3));
            overlay.style.setProperty("--roddo-globe-avatar-pulse-opacity", (0.88 - breathe * 0.42).toFixed(3));
          } else {
            overlay.style.opacity = "0";
            overlay.style.transform = "translate3d(-9999px, -9999px, 0) scale(0.38)";
            overlay.style.setProperty("--roddo-globe-avatar-depth", "0.74");
            overlay.style.setProperty("--roddo-globe-avatar-scale", "0.38");
            overlay.style.setProperty("--roddo-globe-avatar-horizon", "0");
            overlay.style.setProperty("--roddo-globe-avatar-plane", "1");
            overlay.style.setProperty("--roddo-globe-avatar-pulse", "0");
            overlay.style.setProperty("--roddo-globe-avatar-pulse-scale", "1");
            overlay.style.setProperty("--roddo-globe-avatar-pulse-opacity", "0");
          }
        }
      } else if (globeAvatarOverlayRef.current) {
        globeAvatarOverlayRef.current.style.opacity = "0";
        globeAvatarOverlayRef.current.style.transform = "translate3d(-9999px, -9999px, 0)";
      }

      stars.rotation.y += 0.00035;
      renderer.render(scene, camera);
    };

    animate();

    return () => {
      modelInstallCancelled = true;
      if (frameRef.current != null) window.cancelAnimationFrame(frameRef.current);
      if (diveTimerRef.current != null) window.clearTimeout(diveTimerRef.current);

      window.clearTimeout(resizeSoon);
      window.clearTimeout(resizeLater);
      window.clearTimeout(resizeAfterLoad);
      if (resizeRaf) window.cancelAnimationFrame(resizeRaf);
      window.removeEventListener("resize", requestResize);
      window.removeEventListener("orientationchange", requestResize);
      resizeObserver.disconnect();

      if (visualMotionRef.current) {
        visualMotionRef.current.style.transform = "translate3d(0, 0, 0) scale(1)";
      }

      disposeObject(scene);
      renderer.dispose();
      renderer.forceContextLoss?.();

      mount.innerHTML = "";

      cameraRef.current = null;
      rendererRef.current = null;
      earthGroupRef.current = null;
      earthModelRef.current = null;
      locationMarkerRef.current = null;
    };
  }, [baseTarget]);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (isDiving) return;

    event.currentTarget.setPointerCapture?.(event.pointerId);
    pointerRef.current = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      moved: false,
    };
    velocityRef.current = { x: 0, y: 0 };
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const pointer = pointerRef.current;
    if (!pointer || pointer.id !== event.pointerId || isDiving) return;

    const dx = event.clientX - pointer.x;
    const dy = event.clientY - pointer.y;

    pointer.x = event.clientX;
    pointer.y = event.clientY;

    if (Math.abs(dx) + Math.abs(dy) > 2) pointer.moved = true;

    targetRotationRef.current.y += dx * 0.008;
    targetRotationRef.current.x = clamp(targetRotationRef.current.x + dy * 0.004, -0.72, 0.72);

    velocityRef.current.y = dx * 0.0009;
    velocityRef.current.x = dy * 0.00045;
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (pointerRef.current?.id === event.pointerId) pointerRef.current = null;
  };

  return (
    <div
      data-roddo-tour="big-blue-globe"
      className="relative h-full min-h-0 w-full overflow-hidden overscroll-none"
      style={{
        background: shellBg,
        color: "#F4F6FA",
        contain: "layout paint size",
      }}
    >
      <div className="pointer-events-none absolute inset-0 opacity-80">
        <div className="absolute inset-x-0 top-0 h-[34%] bg-[linear-gradient(180deg,rgba(47,128,237,0.10),transparent)] dark:bg-[linear-gradient(180deg,rgba(112,87,232,0.10),transparent)]" />
      </div>

      <div
        className={`pointer-events-none absolute inset-0 z-20 transition-opacity duration-500 ${
          isDiving ? "opacity-100" : "opacity-0"
        }`}
      >
        <div className="absolute inset-0 bg-cyan-200/20 backdrop-blur-[2px]" />
        <div className="absolute left-1/2 top-1/2 h-24 w-24 -translate-x-1/2 -translate-y-1/2 rounded-full bg-cyan-200/80 blur-2xl" />
      </div>

      <div className="relative z-10 mx-auto h-full min-h-0 w-full max-w-[540px] overflow-hidden px-4">
        <div className="pointer-events-none absolute left-1/2 top-[clamp(50px,7vh,70px)] z-10 -translate-x-1/2 text-center">
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/65 drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)]">
            DOUBLE CLICK TO DIVE
          </div>
        </div>

        {/* Big Blue Radio: match MapView placement near the lower-left HUD stack. */}
        <div
          className="absolute left-2 sm:left-4 pointer-events-auto z-30"
          style={{ bottom: 'calc(var(--roddo-nav-h, 72px) + env(safe-area-inset-bottom) + 20px)' }}
        >
          <RoddoRadioMapWidget isDarkMode />
        </div>

        {/* Bottom radio dock: opened/closed from the sound button, same behavior as MapView. */}
        <div
          className="absolute left-[68px] right-[68px] sm:left-[84px] sm:right-[84px] pointer-events-none z-20"
          style={{ bottom: 'calc(var(--roddo-nav-h, 72px) + env(safe-area-inset-bottom) + 8px)' }}
        >
          <RoddoRadioNowPlayingBanner isDarkMode placement="bottom" />
        </div>

        <div
          className="absolute inset-x-4 top-0 flex min-h-0 items-center justify-center overflow-hidden"
          style={{
            // Keep the space background full-bleed, but measure the Earth stage
            // above the BottomNav so the globe does not get pulled into the dock.
            bottom: 'var(--roddo-nav-h, 72px)',
          }}
        >
          <div
            role="button"
            tabIndex={0}
            aria-label="Spin Earth. Double click to dive into Big Blue map."
            className="relative grid aspect-square min-w-[300px] max-w-[500px] touch-none select-none place-items-center outline-none transition-transform duration-300 ease-out translate-y-[var(--roddo-globe-y)]"
            style={{
              width: GLOBE_SIZE_CSS,
              "--roddo-globe-y": "clamp(42px, 5.6vh, 72px)",
            } as React.CSSProperties}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={() => {
              pointerRef.current = null;
            }}
            onDoubleClick={() => doDive(selectedTargetRef.current, { preferUserLocation: true })}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") doDive(selectedTargetRef.current, { preferUserLocation: true });
            }}
          >
            <div
              ref={visualMotionRef}
              className="relative grid h-full min-h-[280px] w-full min-w-[280px] place-items-center"
              style={{
                transform: "translate3d(0, 0, 0) scale(1)",
                transformOrigin: "50% 50%",
                willChange: "transform",
                backfaceVisibility: "hidden",
                WebkitBackfaceVisibility: "hidden",
                contain: "paint",
              }}
            >
              <div
                ref={mountRef}
                className="relative h-full min-h-[280px] w-full min-w-[280px] overflow-hidden rounded-full"
              />

              {currentLocationTarget && (
                <div
                  ref={globeAvatarOverlayRef}
                  className="roddo-globe-avatar-overlay pointer-events-none absolute left-0 top-0 z-30"
                  data-globe-avatar="true"
                  data-globe-avatar-version="v4144-axis-pulse-polish"
                  style={{
                    width: globeAvatarMetrics.iconSize[0],
                    height: globeAvatarMetrics.iconSize[1],
                    opacity: 0,
                    transform: "translate3d(-9999px, -9999px, 0)",
                    willChange: "transform, opacity",
                  }}
                  dangerouslySetInnerHTML={{ __html: globeAvatarHtml }}
                />
              )}

              {loadError && (
                <div className="absolute inset-0 grid place-items-center rounded-full bg-black/72 p-8 text-center text-xs font-black text-white/85 backdrop-blur-sm">
                  Earth.glb did not load. Check /public/models/earth/Earth.glb
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <LoadingScreen
        mode="overlay"
        visible={!isLoaded && !loadError && !roddoEarthLoadedOnce}
        label="Opening Big Blue…"
      />
    </div>
  );
};

export default BigBlueGlobeView;